import { useUserFindOneLazyQuery } from "gql/generated";
import { useCallback, useState } from "react";
import {
  FieldValue,
  FieldValues,
  PathValue,
  UseFormReturn,
} from "react-hook-form";
import { z } from "zod";
import { ControlledInputProps } from "shared/components";

type UseInviteStaffMemberProps<T extends FieldValues> = {
  entityId: string;
  form: UseFormReturn<T>;
  index: number;
  inputNameEmailProps: ControlledInputProps;
  inputNameFirstNameProps: ControlledInputProps;
  inputNameLastNameProps: ControlledInputProps;
};

export const useInviteStaffMember = <T extends FieldValues>({
  entityId,
  form,
  inputNameEmailProps,
  inputNameFirstNameProps,
  inputNameLastNameProps,
}: UseInviteStaffMemberProps<T>) => {
  const [findUserByEmail] = useUserFindOneLazyQuery();

  const [isDuplicateUser, setIsDuplicateUser] = useState(false);
  const [namesDisabled, setNamesDisabled] = useState(true);

  const handleValidateEmail = useCallback(async () => {
    const email = form.getValues(inputNameEmailProps.name as FieldValue<T>);
    const emailSchema = z.string().min(1).email();

    setIsDuplicateUser(false);
    setNamesDisabled(true);
    try {
      emailSchema.parse(email);
      const user = await findUserByEmail({ variables: { filter: { email } } });

      if (user && user.data && user.data.userFindOne?._id) {
        // Check to see if the user is already attached to this entity
        if (
          user.data.userFindOne.access.operators?.find(
            (item) => item?.entityId === entityId,
          )
        ) {
          setIsDuplicateUser(true);
        } else {
          form.setValue(
            inputNameFirstNameProps.name as FieldValue<T>,
            user.data.userFindOne.firstName as PathValue<T, FieldValue<T>>,
          );
          form.setValue(
            inputNameLastNameProps.name as FieldValue<T>,
            user.data.userFindOne.lastName as PathValue<T, FieldValue<T>>,
          );
        }
        form.clearErrors(inputNameFirstNameProps.name as FieldValue<T>);
        form.clearErrors(inputNameLastNameProps.name as FieldValue<T>);
      } else {
        setNamesDisabled(false);
      }
    } catch {
      // Ensure the names are editable
      setNamesDisabled(false);
      form.clearErrors(inputNameFirstNameProps.name as FieldValue<T>);
      form.clearErrors(inputNameLastNameProps.name as FieldValue<T>);
    }
  }, [
    entityId,
    form,
    inputNameEmailProps,
    inputNameFirstNameProps,
    inputNameLastNameProps,
    findUserByEmail,
  ]);

  return {
    isDuplicateUser,
    namesDisabled,
    handleValidateEmail,
  };
};
